import { FC, ReactNode } from 'react'
import styled from '@emotion/styled'
import { Bookmark } from '@emotion-icons/fa-regular/Bookmark'
import { CheckCircle } from '@emotion-icons/fa-solid/CheckCircle'
import { Colors, Shapes } from 'src/components/theme'
import { SidebarResultReturnHeader } from 'src/v2/views/owner-reveal/sidebar/siebar-result-return-header'
import { ButtonWithIcon } from 'src/components/button/button-with-icon'
import { Chip } from 'src/v2/components/chip'
import { BrandLogo } from 'src/components/insights/location-visits/stats/location-logo'
import { Typography } from '@mui/material'
import { rgbaStrOpacity, hexToRGBA } from 'src/utils/colors'
import { useMapExplorerNavigation } from 'src/views/map-explorer/sidebar/search/use-navigation'
import { isFeatureEnabled } from 'src/utils/feature-flag'

const HeaderCoverContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
`

const SearchBarContainer = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  margin: 16px;
  overflow: hidden;
`

const HeaderImage = styled.img<{ color?: string; fit?: 'cover' | 'contain'; filter: 1 | 0 }>`
  height: 180px;
  width: auto;
  object-fit: ${(props) => props.fit || 'cover'};
  border-bottom-right-radius: ${Shapes.borderRadius}px;
  border-right: 1px solid ${Colors.border};
  border-bottom: 1px solid ${Colors.border};
  background: radial-gradient(
    circle farthest-side at 30% 10%,
    ${(props) => props.color || Colors.neutral100},
    ${Colors.neutral50}
  );
`

const HeaderIcon = styled.div<{ iconBackground?: string; iconBorderColor?: string; iconRadius?: 'circle' | 'rounded' }>`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  bottom: 0px;
  left: ${Shapes.spacing[4]}px;
  height: 40px;
  width: 40px;
  transform: translateY(50%);
  border-radius: ${(props) => (props.iconRadius === 'circle' ? '50%' : `${Shapes.borderRadius}px`)};
  border: 2px solid;
  border-color: ${(props) => props.iconBorderColor || Colors.blueMedium};
  background-color: ${(props) => props.iconBackground || Colors.blueLight};
  box-shadow: ${Colors.BOX_SHADOW};
  & > svg {
    height: 16px;
    width: 16px;
  }
`
const HeaderIconLogo = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  bottom: 0px;
  left: ${Shapes.spacing[4]}px;
  height: 50px;
  width: 50px;
  transform: translateY(50%);
  border-radius: ${Shapes.borderRadius}px;
  border: 2px solid ${Colors.border};
  background-color: ${Colors.bgLight};
  overflow: hidden;
  box-shadow: ${Colors.BOX_SHADOW};
  cursor: pointer;
  :hover {
    border-color: ${Colors.borderInput};
  }
`
// aside the header icon by 16px align center
const HeaderIconBadge = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  bottom: 0px;
  left: 64px;
  transform: translateY(50%);
`

const SaveButton = styled(ButtonWithIcon)`
  position: absolute;
  right: ${Shapes.spacing[4]}px;
  bottom: 0;
  transform: translateY(50%);
  min-width: 80px;
`

export type HeaderCoverProps = {
  image?: string | ReactNode
  imageFit?: 'cover' | 'contain'
  icon: React.ReactNode
  iconBackground?: string
  iconBorderColor?: string
  iconRadius?: 'circle' | 'rounded'
  brandId?: string
  badge?: string
  onClickSave?: (evt: React.MouseEvent<HTMLElement>) => void
  saved?: boolean
  savedCount?: number
  backText?: string
  onBackClick?: () => void
  onClose?: () => void
}

export const HeaderCover: FC<HeaderCoverProps> = ({
  icon,
  iconBackground,
  iconBorderColor,
  iconRadius = 'circle',
  brandId,
  badge,
  image,
  imageFit = 'cover',
  onClickSave,
  saved,
  savedCount,
  backText,
  onBackClick,
  onClose,
}) => {
  const isOwnerRevealEnabled = isFeatureEnabled('owner-reveal')
  const { selectBrand } = useMapExplorerNavigation()
  const isHexColor = (color: string) => /^#([0-9A-F]{3}){1,2}$/i.test(color)

  const onBrandClick = (brandId: string) => async () => {
    if (isOwnerRevealEnabled) {
      await selectBrand(brandId)
    }
  }

  return (
    <HeaderCoverContainer>
      {onBackClick && onClose && (
        <SearchBarContainer>
          <SidebarResultReturnHeader
            padding="4px 8px"
            backText={backText}
            onClickBack={onBackClick}
            onClose={onClose}
          />
        </SearchBarContainer>
      )}
      {typeof image === 'string' ? (
        <HeaderImage
          filter={badge === 'Brand' && brandId ? 1 : 0}
          fit={imageFit}
          color={
            iconBackground
              ? isHexColor(iconBackground)
                ? hexToRGBA(iconBackground, 0.15)
                : rgbaStrOpacity(iconBackground, 0.15)
              : undefined
          }
          src={image || ''}
        />
      ) : (
        image
      )}
      {brandId ? (
        <HeaderIconLogo onClick={onBrandClick(brandId)}>
          <BrandLogo brandId={brandId} size={48} noBorder />
        </HeaderIconLogo>
      ) : (
        <HeaderIcon iconBackground={iconBackground} iconBorderColor={iconBorderColor} iconRadius={iconRadius}>
          {icon}
        </HeaderIcon>
      )}
      {badge && (
        <HeaderIconBadge style={{ left: brandId ? '80px' : '64px' }}>
          <Chip backgroundColor={Colors.neutral100} icon={false}>
            <Typography variant="text1m" color={Colors.neutral500}>
              {badge}
            </Typography>
          </Chip>
        </HeaderIconBadge>
      )}
      {!!onClickSave && (
        <SaveButton
          color={saved ? Colors.primaryGreen400 : Colors.primaryBlue400}
          inverted={saved}
          onClick={onClickSave}>
          {saved ? (
            <CheckCircle size={14} color={Colors.bgWhite} />
          ) : (
            <Bookmark size={14} color={Colors.primaryBlue400} />
          )}
          {`${saved ? 'Saved' : 'Save'}`}
          {savedCount && savedCount > 0 ? ` (${savedCount})` : null}
        </SaveButton>
      )}
    </HeaderCoverContainer>
  )
}
